'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { Plus, X, Upload, Vote, Loader2, Heart, CheckCircle, Trophy, Users } from 'lucide-react';
import AppImage from '@/components/ui/AppImage';
import { createClient } from '@/lib/supabase/client';

interface Contestant {
  id: string;
  name: string;
  age: number;
  description: string;
  imageUrl: string;
  imageAlt: string;
  votes: number;
}

const STORAGE_KEY = 'mug_voted_contestant';

interface AddContestantForm {
  name: string;
  age: string;
  description: string;
}

export default function VotingContent() {
  const [contestants, setContestants] = useState<Contestant[]>([]);
  const [votedId, setVotedId] = useState<string | null>(null);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [votingId, setVotingId] = useState<string | null>(null);
  const [showVoteConfirm, setShowVoteConfirm] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [formErrors, setFormErrors] = useState<Partial<AddContestantForm & { image: string }>>({});
  const [totalVotes, setTotalVotes] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [form, setForm] = useState<AddContestantForm>({
    name: '',
    age: '',
    description: '',
  });

  const supabase = createClient();

  // Load contestants from Supabase
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) setVotedId(stored);

    const fetchContestants = async () => {
      setIsLoading(true);
      setLoadError(null);
      try {
        const { data, error } = await supabase
          .from('contestants')
          .select('*')
          .order('votes', { ascending: false });

        if (error) {
          console.error('Failed to load contestants:', error.message);
          setLoadError('Failed to load contestants.');
        } else {
          const mapped: Contestant[] = (data || []).map((row) => ({
            id: row.id,
            name: row.name,
            age: row.age,
            description: row.description,
            imageUrl: row.image_url,
            imageAlt: row.image_alt,
            votes: row.votes,
          }));
          setContestants(mapped);
        }
      } catch (err) {
        console.error('Unexpected error loading contestants:', err);
        setLoadError('Unexpected error loading contestants.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchContestants();
  }, []);

  useEffect(() => {
    setTotalVotes(contestants.reduce((sum, c) => sum + c.votes, 0));
  }, [contestants]);

  const getVotePercentage = useCallback(
    (votes: number) => {
      if (totalVotes === 0) return 0;
      return Math.round((votes / totalVotes) * 100);
    },
    [totalVotes]
  );

  const handleVote = async (contestantId: string) => {
    if (votedId) return;
    setVotingId(contestantId);

    try {
      // Increment vote count in Supabase
      const contestant = contestants.find((c) => c.id === contestantId);
      if (contestant) {
        const { error } = await supabase
          .from('contestants')
          .update({ votes: contestant.votes + 1 })
          .eq('id', contestantId);

        if (error) {
          console.error('Vote update error:', error.message);
        }
      }
    } catch (err) {
      console.error('Vote error:', err);
    }

    setContestants((prev) =>
      prev.map((c) =>
        c.id === contestantId ? { ...c, votes: c.votes + 1 } : c
      )
    );
    setVotedId(contestantId);
    localStorage.setItem(STORAGE_KEY, contestantId);
    setVotingId(null);
    setShowVoteConfirm(contestantId);
    setTimeout(() => setShowVoteConfirm(null), 3500);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const validateForm = () => {
    const errs: Partial<AddContestantForm & { image: string }> = {};
    if (!form.name.trim()) errs.name = 'Name is required';
    if (!form.age || isNaN(Number(form.age))) errs.age = 'Valid age is required';
    if (!form.description.trim()) errs.description = 'Description is required';
    if (!selectedFile) errs.image = 'Contestant image is required';
    setFormErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleAddContestant = async () => {
    if (!validateForm()) return;
    setIsAdding(true);

    try {
      const imageUrl = previewUrl || '/assets/images/12eef6ff-c410-4438-b75b-48bf1bf380ed-1784970622837.png';
      const imageAlt = `${form.name} — Miss Universities of Gauteng 2026 voting contestant`;

      const { data, error } = await supabase
        .from('contestants')
        .insert({
          name: form.name,
          age: Number(form.age),
          description: form.description,
          image_url: imageUrl,
          image_alt: imageAlt,
          votes: 0,
        })
        .select()
        .single();

      if (error) {
        console.error('Add contestant error:', error.message);
        setIsAdding(false);
        return;
      }

      const newContestant: Contestant = {
        id: data.id,
        name: data.name,
        age: data.age,
        description: data.description,
        votes: data.votes,
        imageUrl: data.image_url,
        imageAlt: data.image_alt,
      };

      setContestants((prev) => [...prev, newContestant]);
    } catch (err) {
      console.error('Unexpected error adding contestant:', err);
    }

    setForm({ name: '', age: '', description: '' });
    setSelectedFile(null);
    setPreviewUrl(null);
    setFormErrors({});
    setIsAdding(false);
    setShowAdminPanel(false);
  };

  const handleRemoveContestant = async (id: string) => {
    try {
      const { error } = await supabase.from('contestants').delete().eq('id', id);
      if (error) {
        console.error('Remove contestant error:', error.message);
        return;
      }
    } catch (err) {
      console.error('Unexpected error removing contestant:', err);
    }

    setContestants((prev) => prev.filter((c) => c.id !== id));
    if (votedId === id) {
      setVotedId(null);
      localStorage.removeItem(STORAGE_KEY);
    }
  };

  const leaderContestant = contestants.length > 0
    ? contestants.reduce((a, b) => (a.votes > b.votes ? a : b))
    : null;

  return (
    <div>
      {/* Page Hero */}
      <section style={{ background: '#800000' }} className="py-14 px-4 relative overflow-hidden">
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at center, rgba(212,175,55,0.07) 0%, transparent 60%)' }} />
        <div className="max-w-screen-xl mx-auto text-center relative">
          <div className="text-4xl mb-4 crown-icon" role="img" aria-label="Crown">👑</div>
          <h1 className="font-800 text-3xl sm:text-4xl lg:text-5xl mb-3" style={{ color: '#D4AF37' }}>
            PUBLIC VOTING
          </h1>
          <div className="section-divider mb-4" />
          <p className="text-white/80 text-sm sm:text-base max-w-xl mx-auto mb-6">
            Vote for your favourite contestant. One vote per device only.
          </p>

          {/* Stats row */}
          {contestants.length > 0 && (
            <div className="flex items-center justify-center gap-6 flex-wrap">
              <div
                className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-600"
                style={{ background: 'rgba(212,175,55,0.15)', color: '#D4AF37' }}
              >
                <Users size={16} />
                {contestants.length} Contestants
              </div>
              <div
                className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-600"
                style={{ background: 'rgba(212,175,55,0.15)', color: '#D4AF37' }}
              >
                <Heart size={16} />
                {totalVotes.toLocaleString()} Total Votes
              </div>
              {votedId && (
                <div
                  className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-600"
                  style={{ background: 'rgba(255,255,255,0.15)', color: '#ffffff' }}
                >
                  <CheckCircle size={16} />
                  You have voted
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Admin Bar */}
      <div className="px-4 py-4" style={{ background: '#faf3d0', borderBottom: '1px solid #D4AF37' }}>
        <div className="max-w-screen-xl mx-auto flex items-center justify-between">
          <p className="text-xs font-600" style={{ color: '#800000' }}>
            Admin Panel — {contestants.length} contestant{contestants.length !== 1 ? 's' : ''} added
          </p>
          <button
            onClick={() => setShowAdminPanel(true)}
            className="flex items-center gap-2 px-5 py-2 rounded-full text-sm font-700 btn-gold shadow-gold"
          >
            <Plus size={16} />
            Add Contestant
          </button>
        </div>
      </div>

      {/* Already voted notice */}
      {votedId && contestants.length > 0 && (
        <div
          className="px-4 py-3 text-center text-sm font-600 flex items-center justify-center gap-2"
          style={{ background: '#fff8e6', color: '#800000', borderBottom: '1px solid #D4AF37' }}
        >
          <CheckCircle size={16} />
          You have already cast your vote. Thank you for participating!
        </div>
      )}

      {/* Contestants Grid */}
      <section className="py-14 px-4" style={{ background: '#faf9f7' }}>
        <div className="max-w-screen-xl mx-auto">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-24 text-center">
              <Loader2 size={40} className="animate-spin mb-4" style={{ color: '#D4AF37' }} />
              <p className="text-sm font-500" style={{ color: '#6b6b6b' }}>Loading contestants...</p>
            </div>
          ) : loadError ? (
            <div className="flex flex-col items-center justify-center py-24 text-center">
              <p className="text-sm font-500" style={{ color: '#c0392b' }}>{loadError}</p>
            </div>
          ) : contestants.length === 0 ? (
            /* Empty State */
            <div className="flex flex-col items-center justify-center py-24 text-center">
              <div
                className="w-24 h-24 rounded-full flex items-center justify-center mb-6 shadow-gold"
                style={{ background: 'linear-gradient(135deg, #D4AF37 0%, #f0d060 100%)' }}
              >
                <Vote size={40} color="#800000" />
              </div>
              <h2 className="font-700 text-xl mb-2" style={{ color: '#800000' }}>
                Voting Not Yet Open
              </h2>
              <p className="text-sm max-w-sm leading-relaxed mb-6" style={{ color: '#6b6b6b' }}>
                Contestants for the Miss Universities of Gauteng 2026 public vote will be listed here
                once the voting round opens. Stay tuned!
              </p>
              <button
                onClick={() => setShowAdminPanel(true)}
                className="px-6 py-3 rounded-full text-sm font-700 btn-gold shadow-gold"
              >
                Add First Contestant
              </button>
            </div>
          ) : (
            <>
              {/* Leaderboard hint */}
              {leaderContestant && leaderContestant.votes > 0 && (
                <div
                  className="flex items-center gap-3 px-5 py-3 rounded-xl mb-8 max-w-sm"
                  style={{ background: '#fff8e6', border: '1px solid #D4AF37' }}
                >
                  <Trophy size={20} color="#D4AF37" />
                  <p className="text-sm font-600" style={{ color: '#800000' }}>
                    Leading: <span style={{ color: '#3a3a3a' }}>{leaderContestant.name}</span> with{' '}
                    {leaderContestant.votes} vote{leaderContestant.votes !== 1 ? 's' : ''}
                  </p>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-4 gap-6">
                {contestants.map((contestant, index) => {
                  const hasVotedForThis = votedId === contestant.id;
                  const hasVotedForOther = votedId !== null && votedId !== contestant.id;
                  const percentage = getVotePercentage(contestant.votes);
                  const isVoting = votingId === contestant.id;
                  const justVoted = showVoteConfirm === contestant.id;

                  return (
                    <div
                      key={contestant.id}
                      className="finalist-card rounded-2xl overflow-hidden card-hover group"
                      style={{ animationDelay: `${index * 80}ms` }}
                    >
                      {/* Image */}
                      <div className="relative aspect-[3/4] overflow-hidden">
                        <AppImage
                          src={contestant.imageUrl}
                          alt={contestant.imageAlt}
                          fill
                          className="object-cover transition-transform duration-500 group-hover:scale-105"
                          sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
                        />
                        <div
                          className="absolute inset-0"
                          style={{ background: 'linear-gradient(to top, rgba(128,0,0,0.85) 0%, transparent 50%)' }}
                        />
                        {/* Remove button */}
                        <button
                          onClick={() => handleRemoveContestant(contestant.id)}
                          className="absolute top-3 right-3 w-7 h-7 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                          style={{ background: 'rgba(128,0,0,0.85)' }}
                          aria-label={`Remove ${contestant.name} from voting`}
                        >
                          <X size={14} color="#ffffff" />
                        </button>
                        {/* Voted badge */}
                        {hasVotedForThis && (
                          <div
                            className="absolute top-3 left-3 flex items-center gap-1 px-2 py-1 rounded-full text-xs font-700 voted-badge"
                          >
                            <Heart size={10} fill="currentColor" />
                            YOUR VOTE
                          </div>
                        )}
                        {/* Just voted animation */}
                        {justVoted && (
                          <div
                            className="absolute inset-0 flex items-center justify-center fade-in"
                            style={{ background: 'rgba(128,0,0,0.7)' }}
                          >
                            <div className="text-center">
                              <CheckCircle size={48} color="#D4AF37" />
                              <p className="text-white font-700 text-sm mt-2">Vote Counted!</p>
                            </div>
                          </div>
                        )}
                        {/* Bottom info */}
                        <div className="absolute bottom-0 left-0 right-0 p-4">
                          <p className="text-white font-700 text-base leading-tight">{contestant.name}</p>
                          <p className="text-xs font-500 mt-0.5" style={{ color: '#D4AF37' }}>
                            Age {contestant.age}
                          </p>
                        </div>
                      </div>

                      {/* Card body */}
                      <div className="p-4">
                        <p className="text-sm leading-snug mb-4" style={{ color: '#3a3a3a' }}>
                          {contestant.description}
                        </p>

                        {/* Vote count + bar */}
                        <div className="mb-4">
                          <div className="flex items-center justify-between mb-1.5">
                            <span className="text-xs font-600" style={{ color: '#800000' }}>
                              {contestant.votes} vote{contestant.votes !== 1 ? 's' : ''}
                            </span>
                            <span className="text-xs font-600" style={{ color: '#D4AF37' }}>
                              {percentage}%
                            </span>
                          </div>
                          <div
                            className="w-full h-2 rounded-full overflow-hidden"
                            style={{ background: '#e2ddd6' }}
                          >
                            <div
                              className="h-full rounded-full transition-all duration-700"
                              style={{
                                width: `${percentage}%`,
                                background: 'linear-gradient(90deg, #800000, #D4AF37)',
                              }}
                            />
                          </div>
                        </div>

                        {/* Vote button */}
                        <button
                          onClick={() => !votedId && handleVote(contestant.id)}
                          disabled={votedId !== null || isVoting}
                          className="w-full py-3 rounded-full text-sm font-700 tracking-widest vote-btn flex items-center justify-center gap-2 disabled:cursor-not-allowed"
                        >
                          {isVoting ? (
                            <>
                              <Loader2 size={16} className="animate-spin" />
                              VOTING...
                            </>
                          ) : hasVotedForThis ? (
                            <>
                              <CheckCircle size={16} />
                              VOTED
                            </>
                          ) : hasVotedForOther ? (
                            <>
                              <Vote size={16} />
                              VOTE
                            </>
                          ) : (
                            <>
                              <Heart size={16} />
                              VOTE
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </section>

      {/* Add Contestant Modal */}
      {showAdminPanel && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-4 modal-backdrop"
          onClick={() => setShowAdminPanel(false)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="add-contestant-title"
        >
          <div
            className="bg-white rounded-2xl shadow-card-hover w-full max-w-lg max-h-[90vh] overflow-y-auto scale-in"
            style={{ border: '2px solid #D4AF37' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div
              className="flex items-center justify-between px-6 py-4 sticky top-0 z-10"
              style={{ background: '#800000', borderRadius: '14px 14px 0 0' }}
            >
              <h2 id="add-contestant-title" className="font-700 text-base" style={{ color: '#D4AF37' }}>
                Add Contestant for Voting
              </h2>
              <button
                onClick={() => setShowAdminPanel(false)}
                className="p-1.5 rounded-full hover:bg-white/10 transition-colors"
                aria-label="Close panel"
              >
                <X size={18} color="#ffffff" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-4">
              {/* Image Upload */}
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-600" style={{ color: '#000000' }}>
                  Contestant Image <span style={{ color: '#800000' }}>*</span>
                </label>
                <div
                  className={`upload-zone rounded-xl overflow-hidden ${previewUrl ? 'has-file p-0' : 'p-6'}`}
                  onClick={() => document.getElementById('admin-contestant-img')?.click()}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && document.getElementById('admin-contestant-img')?.click()}
                  aria-label="Upload contestant image"
                >
                  <input
                    id="admin-contestant-img"
                    type="file"
                    accept=".jpg,.jpeg,.png"
                    className="hidden"
                    onChange={handleFileChange}
                  />
                  {previewUrl ? (
                    <div className="relative w-full aspect-[3/4]">
                      <AppImage
                        src={previewUrl}
                        alt="Preview of uploaded contestant photo"
                        fill
                        className="object-cover"
                        unoptimized
                      />
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity" style={{ background: 'rgba(0,0,0,0.4)' }}>
                        <p className="text-white text-sm font-600">Change Image</p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2">
                      <Upload size={28} color="#D4AF37" />
                      <p className="text-sm font-500" style={{ color: '#800000' }}>Upload contestant photo</p>
                      <p className="text-xs" style={{ color: '#9a9a9a' }}>JPG or PNG</p>
                    </div>
                  )}
                </div>
                {formErrors.image && (
                  <p className="text-xs font-500" style={{ color: '#c0392b' }}>{formErrors.image}</p>
                )}
              </div>

              {/* Name */}
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-600" style={{ color: '#000000' }}>
                  Name <span style={{ color: '#800000' }}>*</span>
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                  placeholder="e.g. Nomvula Sithole"
                  className="input-focus w-full px-4 py-3 rounded-lg text-sm border"
                  style={{ borderColor: formErrors.name ? '#c0392b' : '#e2ddd6', color: '#000000' }}
                />
                {formErrors.name && (
                  <p className="text-xs font-500" style={{ color: '#c0392b' }}>{formErrors.name}</p>
                )}
              </div>

              {/* Age */}
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-600" style={{ color: '#000000' }}>
                  Age <span style={{ color: '#800000' }}>*</span>
                </label>
                <input
                  type="number"
                  value={form.age}
                  onChange={(e) => setForm((p) => ({ ...p, age: e.target.value }))}
                  placeholder="e.g. 20"
                  className="input-focus w-full px-4 py-3 rounded-lg text-sm border"
                  style={{ borderColor: formErrors.age ? '#c0392b' : '#e2ddd6', color: '#000000' }}
                />
                {formErrors.age && (
                  <p className="text-xs font-500" style={{ color: '#c0392b' }}>{formErrors.age}</p>
                )}
              </div>

              {/* Description */}
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-600" style={{ color: '#000000' }}>
                  Description <span style={{ color: '#800000' }}>*</span>
                </label>
                <p className="text-xs" style={{ color: '#6b6b6b' }}>
                  Brief bio or why she should win
                </p>
                <textarea
                  rows={3}
                  value={form.description}
                  onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                  placeholder="e.g. 3rd year Law student passionate about gender equality..."
                  className="input-focus w-full px-4 py-3 rounded-lg text-sm border resize-none"
                  style={{ borderColor: formErrors.description ? '#c0392b' : '#e2ddd6', color: '#000000' }}
                />
                {formErrors.description && (
                  <p className="text-xs font-500" style={{ color: '#c0392b' }}>{formErrors.description}</p>
                )}
              </div>

              {/* Submit */}
              <button
                onClick={handleAddContestant}
                disabled={isAdding}
                className="w-full py-3 rounded-full font-700 text-sm tracking-widest btn-gold shadow-gold disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isAdding ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Adding...
                  </>
                ) : (
                  <>
                    <Plus size={16} />
                    Add Contestant
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}